import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext.tsx';
import saccoRebatesService from "@saccoRebatesService";

const QUERY_KEY = 'sacco_rebates';

/**
 * A custom hook to fetch sacco rebates for a specific membership.
 *
 * @param {string} membershipId - The unique identifier for the membership.
 * @return {Object} The result of the `useQuery` hook, including data, loading, and error state.
 */
export function useFetchSaccoRebates(membershipId: string) {
  const { user } = useAuth();
  return useQuery({
    queryKey: [QUERY_KEY, membershipId],
    queryFn: async () => {
      return await saccoRebatesService.fetchAll(user!.id, membershipId);
    },
    enabled: !!user && !!membershipId,
  });
}

/**
 * Hook to manage the creation of a Sacco rebate for a specific membership.
 *
 * @param {string} membershipId - The unique identifier of the membership for which the rebate is being created.
 * @return {UseMutationResult} A mutation object that provides methods and state for creating a Sacco rebate.
 */
export function useCreateSaccoRebate(membershipId: string) {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: SaccoRebateCreateRequest) => {
      return await saccoRebatesService.create(user!.id, membershipId, input);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [QUERY_KEY, membershipId] }),
  });
}

/**
 * Custom hook to update the rebate information for a specific Sacco membership.
 *
 * @param {string} membershipId - The unique identifier for the Sacco membership whose rebate information is being updated.
 * @return {UseMutationResult} Returns a `UseMutationResult` object that allows triggering the mutation and tracking its state.
 */
export function useUpdateSaccoRebate(membershipId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (request: SaccoRebateUpdateRequest) => {
      return await saccoRebatesService.update(request);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [QUERY_KEY, membershipId] }),
  });
}

/**
 * Custom hook to delete a sacco rebate based on a given membership ID.
 *
 * @param {string} membershipId - The ID of the membership associated with the rebate to be deleted.
 * @return {object} A mutation object returned by the `useMutation` hook. This object can be used to trigger the delete operation and monitor its status.
 */
export function useDeleteSaccoRebate(membershipId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      return await saccoRebatesService.delete(id);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [QUERY_KEY, membershipId] }),
  });
}
